// Session-stream projection (fn-47.2) — the heart of SessionView.
//
// Ported from the console's `streams/use-session-stream.ts` reducer
// (console's copy stays untouched; convergence is a follow-up). Wire
// events fold into a `ChatMessage[]` transcript + `RuntimeStatus` +
// pending-approval card + token totals.
//
// **Load-bearing behaviors this file preserves** (each covered by a
// test in `reduce-stream.test.ts`; provenance: the 2026-05-16 dogfood
// + 2026-05-18 elicitation learnings):
//   - Flat SSE frame vs nested `/events` shape normalization at the
//     boundary (`normalizeWireEvent`) — the reducer sees ONE shape.
//   - `processed_at` fallback when `created_at` is missing (MA emits
//     `processed_at` only, today).
//   - `end_turn` is NOT terminal; only `session.status_terminated`
//     maps to `RuntimeStatus 'terminated'`.
//   - `user.tool_confirmation` arrives AFTER its `agent.tool_result`
//     in the MA event log — the decision is inlined onto the matching
//     tool_use row, never appended as a later sibling.
//   - `derivePending` builds the pending approval from
//     `session.status_idle` + `stop_reason.type === 'requires_action'`.
//   - Seed/live merge rebuilds `[...seed, ...alreadyLiveFrames]` so
//     chronology survives a live-before-seed race (.flow memory:
//     seedlive-merge-in-react-hooks-must-2026-05-14).
//   - Duplicate event-id dedupe (SSE reconnect replays after
//     `Last-Event-ID`).
//   - Custom-tool family: `agent.custom_tool_use` correlates by
//     `custom_tool_use_id`; the ack is `user.custom_tool_result`.
//   - MCP-tool family (fn-67): `agent.mcp_tool_use` renders like a
//     built-in tool row but carries an `mcpServerName` tag and
//     correlates by the CANONICAL rule `tool_use_id ?? eventId`
//     (anthropic-direct omits `tool_use_id`); its result is
//     `agent.mcp_tool_result` keyed on `mcp_tool_use_id`. The HITL ack
//     is the shared `user.tool_confirmation` (same correlator id), so
//     no MCP-specific clear path is needed.
//   - Token usage accumulates from `span.model_request_end` (not
//     agent.message).
//   - Ephemeral previews: `agent.message_preview_*` frames are classified
//     on the body's `type` prefix BEFORE durable normalization
//     (`classifyPreviewFrame`), accumulate in a separate `previews` slot
//     keyed `(event_id, index)`, and are dropped + TOMBSTONED when the
//     durable `agent.message` lands. **Change one, change both** — kept in
//     lockstep with the console reducer
//     (`packages/console/src/streams/use-session-stream.ts`).
export function initialStreamState() {
    return {
        messages: [],
        status: 'unknown',
        connection: 'closed',
        pendingApproval: null,
        lastError: null,
        events: [],
        lastEventId: '',
        tokens: { input: 0, output: 0, cacheRead: 0, cacheCreate: 0 },
        expectBootstrapAck: false,
        previews: {},
        previewTombstones: new Set(),
    };
}
const tryStr = (v) => typeof v === 'string' && v.length > 0 ? v : undefined;
/**
 * Derive the MCP server name from a federated tool name by splitting on
 * the LAST `___` separator (`smokeball___create_contact` → `smokeball`)
 * — the precedent used in `gateway-tools/list.tsx` /
 * `auth-verify/tool-scope.ts`. Returns undefined when there is no
 * separator (a bare `name` MCP tool yields no label — by design) or
 * when the separator sits at the start (no server prefix).
 */
const serverFromToolName = (name) => {
    const sep = name.lastIndexOf('___');
    return sep > 0 ? name.slice(0, sep) : undefined;
};
/** Wire prefix of the ephemeral preview family. */
const PREVIEW_TYPE_PREFIX = 'agent.message_preview_';
/**
 * Classify one raw SSE payload as an ephemeral preview frame — the FIRST thing
 * the transport boundary does with a frame, before any durable normalization.
 *
 * **Classification cannot wait:** preview frames carry NO SSE `id:` line, and
 * per the EventSource spec an id-less event still reports the PRECEDING durable
 * event's id on `MessageEvent.lastEventId`. Adapters map that into
 * `SessionEventEnvelope.id`, so a preview reaching `normalizeWireEvent` would
 * be aliased onto an earlier `agent.message`, swallowed by durable-id dedupe,
 * and able to corrupt `lastEventId`. The body's `type` prefix is the only
 * id-independent signal available.
 *
 * Returns `null` outside the preview family (caller continues down the durable
 * path), a typed frame for a well-formed preview, or `{kind: 'unusable'}` for a
 * prefix-matched but malformed payload — still diverted, then dropped.
 */
export function classifyPreviewFrame(payload) {
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload))
        return null;
    const d = payload;
    if (typeof d.type !== 'string' || !d.type.startsWith(PREVIEW_TYPE_PREFIX))
        return null;
    const eventId = tryStr(d.event_id);
    if (eventId === undefined)
        return { kind: 'unusable' };
    switch (d.type) {
        case 'agent.message_preview_start':
            return { kind: 'start', eventId };
        case 'agent.message_preview_delta':
            return typeof d.index === 'number' &&
                Number.isInteger(d.index) &&
                d.index >= 0 &&
                typeof d.text === 'string'
                ? { kind: 'delta', eventId, index: d.index, text: d.text }
                : { kind: 'unusable' };
        case 'agent.message_preview_close':
            return { kind: 'close', eventId };
        default:
            // A future preview kind we don't understand: ephemeral by prefix,
            // so it must never fall through to the durable path.
            return { kind: 'unusable' };
    }
}
/**
 * The in-flight preview to render: the most recently started un-finalized agent
 * message, its per-index buffers joined in ASCENDING index order — the ONE
 * place index order is resolved. Deltas are a PREFIX guarantee, so a partial
 * join is always a valid prefix of the durable text.
 *
 * Null when nothing is streaming, INCLUDING the window between `preview_start`
 * and the first delta (slot open but empty). Null is the view's sole "no
 * preview" representation: an empty buffer surfacing as `''` would render an
 * in-progress bubble holding only an avatar and a caret, and suppress the
 * working indicator that covers exactly that window.
 */
export function selectActivePreview(state) {
    const eventIds = Object.keys(state.previews);
    const eventId = eventIds[eventIds.length - 1];
    if (eventId === undefined)
        return null;
    const buffers = state.previews[eventId] ?? {};
    const text = Object.keys(buffers)
        .map(Number)
        .sort((a, b) => a - b)
        .map((i) => buffers[i] ?? '')
        .join('');
    if (text.length === 0)
        return null;
    return { eventId, text };
}
/**
 * Normalize one adapter envelope into the single internal wire shape.
 *
 * **Two upstream wire shapes** arrive through the adapter (the BFF
 * re-fans both transparently):
 *   1. **Nested** (`{id, type, data: {...rest}, smokeball?}`) — the CP
 *      `GET /sessions/:id/events` enrichment route. Seed events use
 *      this. Used on first paint + refresh.
 *   2. **Flat MA envelope** (`{id, type, created_at?, processed_at?,
 *      content, stop_reason, ...}`) — the live SSE path stringifies
 *      the raw MA event verbatim, no repacking; every payload field
 *      sits at the top level.
 *
 * Everything that isn't `id`/`type`/`smokeball` is copied into a
 * synthetic `data` bag when the nested form is absent. The timestamp
 * is pulled in preference order: top-level `created_at`, nested
 * `data.created_at`, nested `data.processed_at`, top-level
 * `processed_at` (MA emits `processed_at` only, today).
 *
 * Returns null for unusable payloads (non-object, missing `type`) —
 * callers skip the dispatch.
 */
export function normalizeWireEvent(envelope) {
    if (envelope.data === null || typeof envelope.data !== 'object')
        return null;
    const d = envelope.data;
    if (typeof d.type !== 'string')
        return null;
    // Funnel-level backstop: the durable path itself refuses a preview body, so
    // no adapter can alias one onto the preceding durable event id via
    // `envelope.id`. Callers still classify with `classifyPreviewFrame` first.
    if (d.type.startsWith(PREVIEW_TYPE_PREFIX))
        return null;
    // The embedded id wins when present (it IS the MA event id); fall
    // back to the envelope's id so transports that only carry the id
    // out-of-band (SSE `id:` line) still dedupe correctly.
    const id = tryStr(d.id) ?? envelope.id;
    if (typeof id !== 'string' || id.length === 0)
        return null;
    const nestedData = d.data !== undefined && d.data !== null && typeof d.data === 'object' && !Array.isArray(d.data)
        ? d.data
        : null;
    const data = nestedData !== null
        ? nestedData
        : (() => {
            const flat = {};
            for (const [k, v] of Object.entries(d)) {
                if (k === 'id' || k === 'type' || k === 'smokeball')
                    continue;
                flat[k] = v;
            }
            return flat;
        })();
    const createdAtRaw = tryStr(d.created_at) ??
        tryStr(data.created_at) ??
        tryStr(data.processed_at) ??
        tryStr(d.processed_at);
    return {
        id,
        type: d.type,
        data,
        ...(d.smokeball !== null && typeof d.smokeball === 'object' && !Array.isArray(d.smokeball)
            ? { smokeball: d.smokeball }
            : {}),
        ...(createdAtRaw !== undefined ? { createdAt: createdAtRaw } : {}),
    };
}
/**
 * Explicit `session.status_*` wire → `RuntimeStatus` mapping.
 * Unrecognized wire statuses collapse to `'unknown'` — raw wire
 * strings never leak into the projection (fn-47 R6).
 */
export function runtimeStatusOfWire(eventType) {
    switch (eventType) {
        case 'session.status_idle':
            return 'idle';
        case 'session.status_initializing':
            return 'initializing';
        case 'session.status_running':
            return 'running';
        case 'session.status_terminated':
            return 'terminated';
        default:
            return 'unknown';
    }
}
/**
 * The CP wraps every auto-injected context/mount message in a
 * `<system-bootstrap>...</system-bootstrap>` sentinel; detect it
 * client-side (the wire shape is invariant — we own both ends).
 * Exported (internal) so subject derivation (fn-48) shares the ONE
 * sentinel check instead of re-encoding the prefix.
 */
export function isBootstrapContent(text) {
    return /^\s*<system-bootstrap>/.test(text);
}
/** Matches a LEADING `<system-bootstrap>…</system-bootstrap>` block + trailing ws. */
const LEADING_BOOTSTRAP_RE = /^\s*<system-bootstrap>[\s\S]*?<\/system-bootstrap>\s*/;
/**
 * Strip a LEADING `<system-bootstrap>…</system-bootstrap>` block (and the
 * whitespace after it) from a message's text, returning the remainder. fn-72
 * folds first-message grounding into the FIRST user turn as such a leading
 * block, followed by the user's real message — stripping it leaves only what the
 * user typed. Returns the text unchanged when there is no leading block.
 * Exported (internal) so subject derivation shares the ONE stripper.
 */
export function stripLeadingBootstrap(text) {
    return text.replace(LEADING_BOOTSTRAP_RE, '');
}
/**
 * Block-aware user-text extraction (fn-72). MA `user.message` `content` is a
 * text-block array; when grounding rides the first message it is the FIRST block
 * (`<system-bootstrap>…</system-bootstrap>`) and the user's message is the next
 * block. We drop the WHOLE leading grounding BLOCK — robust against a
 * caller-supplied `context_document`/`extra_context` that itself contains a
 * literal `</system-bootstrap>` (a regex over the concatenated string would
 * mis-close on it). Returns the user's visible text + whether a grounding block
 * was present. A string `content` (defensive/legacy) falls back to the regex
 * stripper.
 */
export function extractUserText(payload) {
    if (payload !== null && typeof payload === 'object') {
        const content = payload.content;
        if (Array.isArray(content)) {
            const texts = [];
            for (const block of content) {
                if (block !== null &&
                    typeof block === 'object' &&
                    block.type === 'text' &&
                    typeof block.text === 'string') {
                    texts.push(block.text);
                }
            }
            if (texts.length > 0 && isBootstrapContent(texts[0] ?? '')) {
                return { text: texts.slice(1).join(''), hadBootstrap: true };
            }
            return { text: texts.join(''), hadBootstrap: false };
        }
        if (typeof content === 'string') {
            if (isBootstrapContent(content))
                return { text: stripLeadingBootstrap(content), hadBootstrap: true };
            return { text: content, hadBootstrap: false };
        }
    }
    return { text: '', hadBootstrap: false };
}
/**
 * Pull the concatenated `text`-block content out of a message payload
 * (string `content` or a content-block array). Exported (internal) so
 * the conversation-list preview extraction (fn-49.4) shares the ONE
 * extractor instead of re-encoding the content-block walk.
 */
export function extractText(payload) {
    if (payload === null || typeof payload !== 'object')
        return '';
    const content = payload.content;
    if (typeof content === 'string')
        return content;
    if (Array.isArray(content)) {
        let acc = '';
        for (const block of content) {
            if (block !== null &&
                typeof block === 'object' &&
                block.type === 'text' &&
                typeof block.text === 'string') {
                acc += block.text;
            }
        }
        return acc;
    }
    return '';
}
/**
 * Extended-thinking blocks would ride on `agent.message` as
 * `{type: 'thinking', thinking | text}` content blocks. Inert today
 * (MA emits status-only `agent.thinking` markers) but kept wired.
 */
function extractThinking(payload) {
    if (payload === null || typeof payload !== 'object')
        return '';
    const content = payload.content;
    if (!Array.isArray(content))
        return '';
    let acc = '';
    for (const block of content) {
        if (block === null ||
            typeof block !== 'object' ||
            block.type !== 'thinking') {
            continue;
        }
        const t = block.thinking;
        if (typeof t === 'string') {
            acc += t;
            continue;
        }
        const alt = block.text;
        if (typeof alt === 'string')
            acc += alt;
    }
    return acc;
}
/**
 * Derive a `PendingApproval` from a `session.status_idle` payload with
 * `stop_reason.type === 'requires_action'` + the in-state event log.
 * Returns null when no `event_ids` resolve to an
 * `agent.[custom_|mcp_]tool_use` (defensive — should not happen on a
 * well-formed stream).
 */
export function derivePending(events, stopReason) {
    if (stopReason === null || typeof stopReason !== 'object')
        return null;
    const r = stopReason;
    if (!Array.isArray(r.event_ids))
        return null;
    for (const rawId of r.event_ids) {
        if (typeof rawId !== 'string')
            continue;
        const tu = events.find((e) => e.id === rawId &&
            (e.type === 'agent.tool_use' ||
                e.type === 'agent.custom_tool_use' ||
                e.type === 'agent.mcp_tool_use'));
        if (!tu)
            continue;
        const p = tu.data;
        if (p === null || typeof p !== 'object' || typeof p.name !== 'string') {
            continue;
        }
        // Anthropic-direct doesn't include `tool_use_id` on the
        // agent.tool_use / agent.mcp_tool_use payload — fall back to the
        // event id (same canonical rule as the platform's webhook handler,
        // handle-idled.ts:195-198). Custom tools (ask_user_question) carry
        // `custom_tool_use_id` instead (MCP never does).
        const toolUseId = tryStr(p.tool_use_id) ?? tryStr(p.custom_tool_use_id) ?? tu.id;
        return {
            eventId: tu.id,
            toolUseId,
            toolName: p.name,
            input: p.input !== undefined &&
                p.input !== null &&
                typeof p.input === 'object' &&
                !Array.isArray(p.input)
                ? p.input
                : {},
        };
    }
    return null;
}
/**
 * Fold one ephemeral preview frame into the preview slot. Every unusable / late
 * / orphan frame returns the SAME state object so React bails out of the
 * re-render.
 *
 * Rules, in order:
 *   - tombstoned `event_id` (its durable `agent.message` already landed) →
 *     ignored. The race-free half of durable-wins: late deltas can never
 *     resurrect a finalized message.
 *   - `start` → open an empty buffer map. A repeat start for a live id is a
 *     no-op (never wipe accumulated text; MA event ids are unique).
 *   - `delta` for an id with no open buffer (start missed / reconnected
 *     mid-turn) → ignored.
 *   - `close` → drop the buffers. Covers the errored turn where the durable
 *     `agent.message` never arrives at all.
 */
function reducePreview(state, frame) {
    if (frame.kind === 'unusable')
        return state;
    if (state.previewTombstones.has(frame.eventId))
        return state;
    const buffers = state.previews[frame.eventId];
    if (frame.kind === 'start') {
        if (buffers !== undefined)
            return state;
        // At most ONE preview is in flight per session, so a start REPLACES the
        // whole map — dropping any buffer left by a turn that ended without a
        // durable message and without a close (dropped frame, or the CP degrading
        // to its poll fallback mid-turn). Keeping it would leave
        // `selectActivePreview` a stale buffer to render as a ghost bubble.
        return { ...state, previews: { [frame.eventId]: {} } };
    }
    if (frame.kind === 'close') {
        if (buffers === undefined)
            return state;
        const { [frame.eventId]: _dropped, ...rest } = state.previews;
        return { ...state, previews: rest };
    }
    if (buffers === undefined)
        return state;
    return {
        ...state,
        previews: {
            ...state.previews,
            [frame.eventId]: { ...buffers, [frame.index]: (buffers[frame.index] ?? '') + frame.text },
        },
    };
}
export function reduceStream(state, action) {
    if (action.type === 'reset')
        return initialStreamState();
    if (action.type === 'connection') {
        // Preview buffers are scoped to ONE transport connection: previews never
        // replay after a reconnect (MA's contract) and a `preview_close` in flight
        // when the connection drops is lost, so a transition MUST discard them.
        // Tombstones and durable state survive untouched.
        return { ...state, connection: action.state, previews: {} };
    }
    if (action.type === 'preview')
        return reducePreview(state, action.frame);
    if (action.type === 'seed') {
        // History replay from `getSessionEvents`. The seed is ALWAYS older
        // than any live frames we may already hold — server-side semantics
        // guarantee `/events` returns up to the moment the request was
        // served and the live stream delivers from that moment forward.
        // Rebuild the event log as `[...history, ...alreadyReceivedLive]`
        // (dedupe by id), then re-fold from a fresh base, so the rendered
        // projection reads in true chronological order regardless of
        // arrival order (.flow memory: seedlive-merge-in-react-hooks).
        const seenIds = new Set();
        const orderedEvents = [];
        for (const ev of action.events) {
            if (seenIds.has(ev.id))
                continue;
            seenIds.add(ev.id);
            orderedEvents.push(ev);
        }
        for (const ev of state.events) {
            if (seenIds.has(ev.id))
                continue;
            seenIds.add(ev.id);
            orderedEvents.push(ev);
        }
        // Preserve the connection flag so an `open`/`connecting` transition that
        // already landed survives the seed merge — and the ephemeral preview slot,
        // so a seed landing mid-turn doesn't blank the in-progress bubble. Any
        // preview whose durable copy is IN the seed is dropped + tombstoned below.
        const base = {
            ...initialStreamState(),
            connection: state.connection,
            previews: state.previews,
            previewTombstones: state.previewTombstones,
        };
        let next = base;
        for (const ev of orderedEvents) {
            next = reduceStream(next, { type: 'event', event: ev, lastEventId: ev.id });
        }
        return next;
    }
    const ev = action.event;
    // Dedupe by id (the SSE transport can replay events on reconnect
    // with `Last-Event-ID`; the same event may arrive again). One
    // upgrade case: live MA delivers some events (acks) UNSTAMPED while
    // the `/events` replay carries `processed_at` — when a stamped copy
    // of an unstamped stored event arrives, adopt its timestamp so turn
    // timings don't stay suppressed until a full remount (live e2e
    // finding, 2026-06-12). The projection itself is not re-folded —
    // only the event log's stamp is upgraded.
    const existingIdx = state.events.findIndex((e) => e.id === ev.id);
    if (existingIdx >= 0) {
        const existing = state.events[existingIdx];
        if (existing !== undefined && existing.createdAt === undefined && ev.createdAt !== undefined) {
            const upgraded = [...state.events];
            upgraded[existingIdx] = { ...existing, createdAt: ev.createdAt };
            // Re-fold the stamp onto the PROJECTION too: the confirmation's
            // first (unstamped) pass set `decision` without `decidedAt` on
            // the tool row / standalone confirmation row — without this, the
            // rendered decision keeps missing its timestamp until a remount.
            let upgradedMessages = state.messages;
            if (ev.type === 'user.tool_confirmation') {
                const stamp = ev.createdAt;
                const toolUseId = ev.data.tool_use_id;
                if (typeof toolUseId === 'string') {
                    upgradedMessages = state.messages.map((m) => {
                        if (m.kind === 'tool_use' && m.toolUseId === toolUseId && m.decision !== undefined) {
                            return m.decidedAt === undefined ? { ...m, decidedAt: stamp } : m;
                        }
                        if (m.kind === 'tool_confirmation' && m.id === ev.id && m.decidedAt === undefined) {
                            return { ...m, decidedAt: stamp };
                        }
                        return m;
                    });
                }
            }
            return {
                ...state,
                events: upgraded,
                messages: upgradedMessages,
                lastEventId: action.lastEventId,
            };
        }
        return { ...state, lastEventId: action.lastEventId };
    }
    let { messages, status, pendingApproval, lastError, tokens, expectBootstrapAck, previews, previewTombstones, } = state;
    switch (ev.type) {
        case 'user.message': {
            // fn-72 — grounding rides the FIRST user message as a LEADING
            // `<system-bootstrap>…</system-bootstrap>` BLOCK, followed by the user's
            // real message. Drop the whole grounding block (block-aware, so an embedded
            // `</system-bootstrap>` in caller context can't mis-close it) and show only
            // the user's text. A message that is ONLY a bootstrap block (the legacy
            // fn-67 standalone turn) strips to empty → stays hidden AND arms the ack-tag
            // so the agent's confirmation reply is suppressed. A GROUNDED first message
            // (real text remains) is a normal user turn whose answer the agent SHOWS —
            // so it must NOT arm the ack tag.
            const { text: stripped, hadBootstrap } = extractUserText(ev.data);
            const isBootstrapOnly = hadBootstrap && stripped.trim().length === 0;
            messages = [
                ...messages,
                {
                    kind: 'user',
                    id: ev.id,
                    content: stripped,
                    ...(isBootstrapOnly ? { isBootstrap: true } : {}),
                },
            ];
            expectBootstrapAck = isBootstrapOnly;
            break;
        }
        case 'agent.message': {
            const usage = ev.data.usage;
            const usageOut = {};
            if (usage !== null && typeof usage === 'object') {
                const u = usage;
                if (typeof u.input_tokens === 'number')
                    usageOut.input = u.input_tokens;
                if (typeof u.output_tokens === 'number')
                    usageOut.output = u.output_tokens;
            }
            const isAck = expectBootstrapAck;
            const thinking = extractThinking(ev.data);
            messages = [
                ...messages,
                {
                    kind: 'assistant',
                    id: ev.id,
                    content: extractText(ev.data),
                    ...(usageOut.input !== undefined || usageOut.output !== undefined
                        ? { usage: usageOut }
                        : {}),
                    ...(isAck ? { isBootstrapAck: true } : {}),
                    ...(thinking.length > 0 ? { thinking } : {}),
                },
            ];
            expectBootstrapAck = false;
            // Durable wins: drop this id's preview buffers and TOMBSTONE the id. The
            // tombstone makes the swap race-free — a delta that lost the race
            // upstream can no longer reopen the preview.
            if (previews[ev.id] !== undefined) {
                const { [ev.id]: _dropped, ...rest } = previews;
                previews = rest;
            }
            if (!previewTombstones.has(ev.id)) {
                previewTombstones = new Set(previewTombstones).add(ev.id);
            }
            break;
        }
        case 'agent.custom_tool_use':
        case 'agent.tool_use': {
            const p = ev.data;
            // Anthropic-direct doesn't include `tool_use_id` on the event
            // payload; `agent.custom_tool_use` carries the correlator under
            // `custom_tool_use_id` instead. Both fall back to the event id
            // (the platform's webhook handler does the same).
            if (typeof p.name === 'string') {
                const toolUseId = tryStr(p.tool_use_id) ?? tryStr(p.custom_tool_use_id) ?? ev.id;
                messages = [
                    ...messages,
                    {
                        kind: 'tool_use',
                        id: ev.id,
                        toolUseId,
                        toolName: p.name,
                        input: p.input !== undefined &&
                            p.input !== null &&
                            typeof p.input === 'object' &&
                            !Array.isArray(p.input)
                            ? p.input
                            : {},
                    },
                ];
            }
            break;
        }
        case 'agent.mcp_tool_use': {
            // MCP tool calls render like a built-in tool row, but they
            // correlate by the CANONICAL rule `tool_use_id ?? eventId` —
            // NOT the custom-tool branch (MCP never carries
            // `custom_tool_use_id`). Anthropic-direct omits `tool_use_id`,
            // so the event id is the correlator (matches the backend's
            // approvals_audit.provider_tool_use_id, handle-idled.ts:195-198).
            const p = ev.data;
            if (typeof p.name === 'string') {
                const toolUseId = tryStr(p.tool_use_id) ?? ev.id;
                // Server name: explicit `mcp_server_name` (live dev-aus wire),
                // else split the federated `name` on the LAST `___`. A bare
                // `name` MCP tool yields no label — that is fine.
                const mcpServerName = tryStr(p.mcp_server_name) ?? serverFromToolName(p.name);
                messages = [
                    ...messages,
                    {
                        kind: 'tool_use',
                        id: ev.id,
                        toolUseId,
                        toolName: p.name,
                        input: p.input !== undefined &&
                            p.input !== null &&
                            typeof p.input === 'object' &&
                            !Array.isArray(p.input)
                            ? p.input
                            : {},
                        ...(mcpServerName !== undefined ? { mcpServerName } : {}),
                    },
                ];
            }
            break;
        }
        case 'agent.tool_result': {
            const p = ev.data;
            if (typeof p.tool_use_id === 'string') {
                const output = extractText({ content: p.content });
                // Bundle into the matching tool_use row when present (one card
                // per call). Fall back to a standalone tool_result row when
                // the originating tool_use is missing (replay race, partial
                // history) so the result isn't lost.
                const idx = messages.findIndex((m) => m.kind === 'tool_use' && m.toolUseId === p.tool_use_id);
                if (idx >= 0) {
                    const tu = messages[idx];
                    const updated = {
                        ...tu,
                        result: {
                            id: ev.id,
                            output,
                            ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                        },
                    };
                    messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
                }
                else {
                    const next = {
                        kind: 'tool_result',
                        id: ev.id,
                        toolUseId: p.tool_use_id,
                        output,
                        ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                    };
                    messages = [...messages, next];
                }
            }
            break;
        }
        case 'agent.mcp_tool_result': {
            // Mirror agent.tool_result, but the result carries its own
            // correlator field `mcp_tool_use_id` (read it first, falling
            // back to `tool_use_id` for wire variance). Matches the same
            // `tool_use` row's `toolUseId` (tool_use_id ?? eventId).
            const p = ev.data;
            const correlator = tryStr(p.mcp_tool_use_id) ?? tryStr(p.tool_use_id);
            if (correlator !== undefined) {
                const output = extractText({ content: p.content });
                const idx = messages.findIndex((m) => m.kind === 'tool_use' && m.toolUseId === correlator);
                if (idx >= 0) {
                    const tu = messages[idx];
                    const updated = {
                        ...tu,
                        result: {
                            id: ev.id,
                            output,
                            ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                        },
                    };
                    messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
                }
                else {
                    // Originating mcp_tool_use missing (replay race / partial
                    // history) — keep the result as a standalone row instead of
                    // dropping it, mirroring the built-in fallback.
                    const next = {
                        kind: 'tool_result',
                        id: ev.id,
                        toolUseId: correlator,
                        output,
                        ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                    };
                    messages = [...messages, next];
                }
            }
            break;
        }
        case 'user.custom_tool_result': {
            // Operator's elicitation answer. MA emits this AFTER the
            // matching `agent.custom_tool_use` requires_action gate is
            // resolved. `data.content[].text` carries the JSON-stringified
            // `{questions, answers}` form-data; bundle it into the tool_use
            // row's `result` so the transcript renders Q→A pairs inline.
            const p = ev.data;
            if (typeof p.custom_tool_use_id !== 'string')
                break;
            const output = extractText({ content: p.content });
            const idx = messages.findIndex((m) => m.kind === 'tool_use' && m.toolUseId === p.custom_tool_use_id);
            if (idx >= 0) {
                const tu = messages[idx];
                const updated = {
                    ...tu,
                    result: {
                        id: ev.id,
                        output,
                        ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                    },
                };
                messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
            }
            else {
                // Originating custom_tool_use missing (replay race / partial
                // history) — keep the answer as a standalone tool_result row
                // instead of dropping it, mirroring the built-in fallback.
                const next = {
                    kind: 'tool_result',
                    id: ev.id,
                    toolUseId: p.custom_tool_use_id,
                    output,
                    ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
                };
                messages = [...messages, next];
            }
            // The custom-tool ack resolves the requires_action gate (the
            // custom family has no user.tool_confirmation sibling) — clear
            // the pending approval here, mirroring the built-in path, so the
            // card doesn't linger until session.status_running.
            if (pendingApproval !== null && pendingApproval.toolUseId === p.custom_tool_use_id) {
                pendingApproval = null;
            }
            break;
        }
        case 'user.tool_confirmation': {
            const p = ev.data;
            if (typeof p.tool_use_id === 'string' && (p.result === 'allow' || p.result === 'deny')) {
                // Correlate back to the originating agent.tool_use event so we
                // can pick up the smokeball annotation (decision_actor_user_id)
                // from `/events` enrichment. The live stream is raw MA and
                // does not carry the annotation; the seed does.
                const originating = state.events.find((e) => (e.type === 'agent.tool_use' || e.type === 'agent.mcp_tool_use') &&
                    (e.id === p.tool_use_id ||
                        e.data.tool_use_id === p.tool_use_id));
                const decidedBy = originating !== undefined &&
                    originating.smokeball !== undefined &&
                    typeof originating.smokeball
                        .decision_actor_user_id === 'string'
                    ? originating.smokeball.decision_actor_user_id
                    : undefined;
                const denyMessage = tryStr(p.deny_message);
                // Annotate the matching tool_use row instead of pushing a new
                // sibling: MA emits user.tool_confirmation at resume time —
                // typically AFTER the matching agent.tool_result has already
                // landed. A sibling row would render the decision visually
                // after the result; inlining collapses the gap.
                const idx = messages.findIndex((m) => m.kind === 'tool_use' && m.toolUseId === p.tool_use_id);
                if (idx >= 0) {
                    const tu = messages[idx];
                    const updated = {
                        ...tu,
                        decision: p.result,
                        ...(ev.createdAt !== undefined ? { decidedAt: ev.createdAt } : {}),
                        ...(decidedBy !== undefined ? { decidedBy } : {}),
                        ...(denyMessage !== undefined ? { denyMessage } : {}),
                    };
                    messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
                }
                else {
                    // Preserve the decision even when the originating tool_use
                    // row never landed (partial history replay, wire drift) —
                    // otherwise the approval would silently disappear.
                    messages = [
                        ...messages,
                        {
                            kind: 'tool_confirmation',
                            id: ev.id,
                            toolUseId: p.tool_use_id,
                            decision: p.result,
                            ...(ev.createdAt !== undefined ? { decidedAt: ev.createdAt } : {}),
                            ...(decidedBy !== undefined ? { decidedBy } : {}),
                            ...(denyMessage !== undefined ? { denyMessage } : {}),
                        },
                    ];
                }
                // Clear pending if this confirmation matches the gate.
                if (pendingApproval !== null && pendingApproval.toolUseId === p.tool_use_id) {
                    pendingApproval = null;
                }
            }
            break;
        }
        case 'session.status_idle': {
            // NOT terminal — `stop_reason.type === 'end_turn'` idles the
            // session but the operator can still send a follow-up. Only
            // `session.status_terminated` ends it.
            status = 'idle';
            const sr = ev.data.stop_reason;
            const srType = sr?.type;
            // The idle frame typically lands right after its agent.tool_use;
            // include the just-arrived event in the lookup.
            pendingApproval =
                srType === 'requires_action' ? derivePending([...state.events, ev], sr) : null;
            break;
        }
        case 'session.status_running': {
            status = 'running';
            pendingApproval = null;
            break;
        }
        case 'session.status_terminated': {
            status = 'terminated';
            pendingApproval = null;
            break;
        }
        case 'session.status_initializing': {
            status = 'initializing';
            break;
        }
        case 'span.model_request_end': {
            // MA records per-turn LLM usage on this event; aggregate into
            // the live token counter. Field names per the 2026-05-16 wire
            // dump: `model_usage.{input_tokens, output_tokens,
            // cache_read_input_tokens, cache_creation_input_tokens}`.
            const usage = ev.data.model_usage;
            if (usage !== null && typeof usage === 'object') {
                const u = usage;
                tokens = {
                    input: tokens.input + (typeof u.input_tokens === 'number' ? u.input_tokens : 0),
                    output: tokens.output + (typeof u.output_tokens === 'number' ? u.output_tokens : 0),
                    cacheRead: tokens.cacheRead +
                        (typeof u.cache_read_input_tokens === 'number' ? u.cache_read_input_tokens : 0),
                    cacheCreate: tokens.cacheCreate +
                        (typeof u.cache_creation_input_tokens === 'number' ? u.cache_creation_input_tokens : 0),
                };
            }
            break;
        }
        case 'session.error': {
            const p = ev.data;
            lastError = {
                type: typeof p.type === 'string' ? p.type : 'unknown',
                message: typeof p.message === 'string' ? p.message : '',
            };
            break;
        }
        default:
            // Unrecognized `session.status_*` wire values collapse to
            // 'unknown' — never leak the raw string into the projection.
            // Other unknown event types are kept in the log but don't change
            // the projection (forward-compatible with new MA event kinds).
            if (ev.type.startsWith('session.status_')) {
                status = runtimeStatusOfWire(ev.type);
            }
            break;
    }
    return {
        ...state,
        messages,
        status,
        pendingApproval,
        lastError,
        tokens,
        expectBootstrapAck,
        previews,
        previewTombstones,
        events: [...state.events, ev],
        lastEventId: action.lastEventId,
    };
}
//# sourceMappingURL=reduce-stream.js.map